import { getListingStatus } from '../utils/listingStatus';
import { useTranslation } from 'react-i18next';

/**
 * Canonical listing-status badge.
 *
 * Single source of truth for status display across the platform.
 * Supports two ergonomic call shapes:
 *   <ListingStatusBadge status={listing.status} />   ← preferred (per spec)
 *   <ListingStatusBadge listing={listing} />         ← granular (covers
 *                                                     "expiring soon" and
 *                                                     reads expires_at).
 *
 * Status priority (when both sources present):
 *   deleted > sold > hidden > expired > expiring > active
 *
 * Active listings render nothing by default (the page typically shows
 * "Published X ago" instead). Pass `showActive` to opt in to an "Active" pill.
 */

// Centralized style + label config. Add new statuses here only.
// `labelKey` falls back to `defaultLabel` when the i18n key is missing.
const STATUS_CONFIG = {
  deleted: {
    labelKey: 'listing.status.deleted',
    defaultLabel: 'Deleted',
    palette: 'bg-text_muted text-white border border-text_muted',
  },
  sold: {
    labelKey: 'listing.status.sold',
    defaultLabel: 'Vendu',
    palette: 'bg-success-soft text-success border border-success',
  },
  hidden: {
    labelKey: 'listing.status.hidden',
    defaultLabel: 'Masqué',
    palette: 'bg-warning-soft text-warning border border-warning',
  },
  expired: {
    labelKey: 'listing.status.expired',
    defaultLabel: 'Expirée',
    palette: 'bg-danger-soft text-danger border border-danger',
  },
  // Transitional state — only reachable via the `listing` prop variant.
  expiring: {
    palette: 'bg-warning-soft text-warning border border-warning',
  },
  active: {
    labelKey: 'listing.status.active',
    defaultLabel: 'Active',
    palette: 'bg-success-soft text-success border border-success',
  },
  rejected: {
    labelKey: 'listing.status.rejected',
    defaultLabel: 'Rejected',
    palette: 'bg-danger-soft text-danger border border-danger',
  },
  removed: {
    labelKey: 'listing.status.removed',
    defaultLabel: 'Removed',
    palette: 'bg-danger-soft text-danger border border-danger',
  },
};

const SHARED_CLASSES =
  'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap';

export const ListingStatusBadge = ({
  status,
  listing,
  showActive = false,
  className = '',
}) => {
  const { t } = useTranslation();

  // Resolve the effective status. When `listing` is provided we delegate to
  // `getListingStatus` (which knows about expires_at and surfaces `expiring`).
  // Otherwise we use the explicit `status` string per the canonical API.
  let effectiveStatus = null;
  let dynamicLabel = null;

  if (listing) {
    const info = getListingStatus(listing, t);
    effectiveStatus = info.status;
    // Only the transitional "expiring" state needs the dynamic days-left label
    // produced by getListingStatus. Every other status pulls its label from
    // the centralized STATUS_CONFIG below.
    if (info.status === 'expiring') dynamicLabel = info.label;
  } else if (status) {
    effectiveStatus = status;
  }

  if (!effectiveStatus) return null;

  const cfg = STATUS_CONFIG[effectiveStatus];
  if (!cfg) return null;

  // Active is rendered only on opt-in (spec: "show nothing OR optional Active").
  if (effectiveStatus === 'active' && !showActive) return null;

  const label = dynamicLabel ?? t(cfg.labelKey, cfg.defaultLabel);
  if (!label) return null;

  return (
    <span
      className={`${SHARED_CLASSES} ${cfg.palette} ${className}`}
      data-testid={`listing-status-badge-${effectiveStatus}`}
    >
      {label}
    </span>
  );
};
